import {
  ANCILLARY_OPTION_SEPARATOR,
  buildAddedAncillaryItem,
  buildDateTimeLabel,
  buildGuestsCountLine,
  buildOptionsLine,
  filterAncillaryItems,
  getAncillaryProductName,
  IAddedAncillaryItem,
  IPotentialBookingAncillaryItem,
} from './addedAncillaryShared';

const EXPERIENCE_NAME_SEPARATOR = ' - ';
const EXPERIENCE_OPTION_SEPARATOR = ' - ';
const EXPERIENCE_BOOLEAN_OPTION_VALUE = 'Yes';

export interface IAddedAncillaryExperience extends IAddedAncillaryItem {
  baseProductName?: string;
  variantName?: string;
  cancellationPolicy?: string | null;
}

export interface IPotentialBookingExperience extends IPotentialBookingAncillaryItem {}

export const buildAncillaryExperienceName = (experience: IPotentialBookingExperience): string => {
  const ancillaryProduct = experience.ancillaryData?.product;
  const composedName = [ancillaryProduct?.baseProductName, ancillaryProduct?.variantName]
    .filter(Boolean)
    .join(EXPERIENCE_NAME_SEPARATOR);

  return composedName === '' ? experience.product?.name ?? experience.title : composedName;
};

export const buildAddedAncillaryExperience = (experience: IPotentialBookingExperience): IAddedAncillaryExperience => {
  const ancillaryProduct = experience.ancillaryData?.product;

  return {
    ...buildAddedAncillaryItem(experience, 'added-ancillary-experience', buildAncillaryExperienceName(experience)),
    baseProductName: ancillaryProduct?.baseProductName,
    variantName: ancillaryProduct?.variantName ?? undefined,
    cancellationPolicy: experience.cancellationPolicy,
  };
};

export const filterAncillaryExperiences = (experiences: unknown[]): IPotentialBookingExperience[] =>
  filterAncillaryItems(experiences);

export const buildAddedAncillaryExperiences = (experiences: unknown[]): IAddedAncillaryExperience[] =>
  filterAncillaryExperiences(experiences).map(buildAddedAncillaryExperience);

export const buildExperienceGuestsLine = (experience: IAddedAncillaryExperience): string =>
  buildGuestsCountLine(experience.numberOfAdults, experience.numberOfChildren).toLowerCase();

export const buildExperienceDateLine = (experience: IAddedAncillaryExperience): string =>
  buildDateTimeLabel({ date: experience.date });

export const buildExperienceOptionsLine = (
  experience: IAddedAncillaryExperience,
  separator: string = EXPERIENCE_OPTION_SEPARATOR
): string =>
  buildOptionsLine(
    experience.options.map(option =>
      option.isBoolean ? { ...option, isBoolean: false, value: EXPERIENCE_BOOLEAN_OPTION_VALUE } : option
    ),
    separator
  );

export const buildExperienceDateAndGuestsLine = (experience: IAddedAncillaryExperience): string => {
  const hasGuests = experience.numberOfAdults > 0 || experience.numberOfChildren > 0;
  const parts = [
    buildExperienceDateLine(experience),
    hasGuests ? buildGuestsCountLine(experience.numberOfAdults, experience.numberOfChildren) : '',
  ].filter(Boolean);

  return parts.length === 0 ? '' : `${parts.join(' - ')}.`;
};

export const buildAncillaryExperienceDetailLines = (experience: IAddedAncillaryExperience): string[] =>
  [
    getAncillaryProductName(experience),
    buildExperienceDateAndGuestsLine(experience),
    buildExperienceOptionsLine(experience, ANCILLARY_OPTION_SEPARATOR),
  ].filter(line => line !== '');
